import React, { useEffect, useMemo } from 'react';
import type { FC } from 'react';
import { join } from 'node:path';
import type { SelectOption } from '@wix/cli-ui-kit';
import { InputState, Link, SelectInput, TextInput } from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { BACKEND_DIR } from '@wix/cli-app-definitions';
import { useWizardStep } from '../Wizard.js';
import type { ApiType } from './Api.js';
import { useWixCliAppBi } from '../../../bi/useWixCliAppBi.js';
import { useBackendComponents } from '../../../backend/useBackendComponents.js';
import {
  composeValidations,
  pathValidation,
  useValidation,
} from '../../../dev-center/components/validations/index.js';
import type { DocsExtensionType } from '../docsExtensionType.js';

export const SelectApiTypeStep: FC<{
  onSubmit: (apiType: ApiType) => void;
  docsExtensionType?: DocsExtensionType;
}> = ({ onSubmit, docsExtensionType }) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const { isActive, goToNextStep } = useWizardStep();

  const webMethodOption = useMemo<SelectOption<ApiType>>(
    () => ({
      title: t('generate_command.api.web_method'),
      description: t('generate_command.api.web_method.description'),
      value: 'WebMethod',
    }),
    [t]
  );

  const httpFunctionOption = useMemo<SelectOption<ApiType>>(
    () => ({
      title: t('generate_command.api.http_function'),
      description: t('generate_command.api.http_function.description'),
      value: 'HttpFunction',
    }),
    [t]
  );

  const apiOption = useMemo(() => {
    if (docsExtensionType === 'HTTP_FUNCTION') {
      return InputState.Submitted(httpFunctionOption);
    }
    if (docsExtensionType === 'WEB_METHOD') {
      return InputState.Submitted(webMethodOption);
    }
  }, [docsExtensionType, httpFunctionOption, webMethodOption]);

  const handleSubmit = (option: SelectOption<ApiType>) => {
    bi.cliFlowStepAnswered({
      question: t('generate_command.api.choose_type'),
      questionKey: 'generate_command.api.choose_type',
      answer: option.title,
      additionalInfo: { extensionType: option.value },
    });
    onSubmit(option.value);
    goToNextStep();
  };

  useEffect(() => {
    if (apiOption) {
      handleSubmit(apiOption.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SelectInput<ApiType>
      isActive={isActive}
      value={apiOption}
      info={
        <Trans
          i18nKey="generate_command.api.choose_type.info"
          // https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/wix-cli/supported-extensions/backend-extensions/about-cli-app-backend-extensions#web-method-extensions
          components={[<Link url="https://wix.to/pX4xyEn" />]}
        />
      }
      label={t('generate_command.api.choose_type')}
      options={[webMethodOption, httpFunctionOption]}
      onSubmit={handleSubmit}
    />
  );
};

export const ApiFolderStep: FC<{
  onSubmit: (folderPath: string) => void;
}> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const { cliFlowStepAnswered } = useWixCliAppBi();
  const { isActive, goToNextStep } = useWizardStep();
  const { backendComponentsByType } = useBackendComponents();

  const apis = backendComponentsByType.Api;

  const validateApiFolder = useValidation(
    composeValidations([
      [
        (folderName: string) =>
          apis.every((api) => api.title !== folderName) &&
          pathValidation(folderName),
        t('generate_command.api.choose_api_name.error'),
      ],
    ])
  );

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.api.choose_api_name')}
      placeholder={t('generate_command.api.api_placeholder')}
      onSubmit={(value) => {
        cliFlowStepAnswered({
          question: t('generate_command.api.choose_api_name'),
          questionKey: 'generate_command.api.choose_api_name',
          answer: value,
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateApiFolder}
    />
  );
};

export const WebMethodFolderStep: FC<{
  onSubmit: (folderPath: string) => void;
}> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const { cliFlowStepAnswered } = useWixCliAppBi();
  const { isActive, goToNextStep } = useWizardStep();
  const { backendComponentsByType } = useBackendComponents();

  const webMethods = backendComponentsByType.WebMethod;

  const validateWebMethodFolder = useValidation(
    composeValidations([
      [
        (filePath: string) => {
          const resolvedFolderPath = `${join(BACKEND_DIR, filePath)}.web.ts`;

          return (
            pathValidation(filePath) &&
            webMethods.every(
              (webMethod) => webMethod.relativePath !== resolvedFolderPath
            )
          );
        },
        t('generate_command.web_method.choose_filename.error'),
      ],
    ])
  );

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.web_method.choose_filename')}
      placeholder={t('generate_command.web_method.choose_filename.placeholder')}
      onSubmit={(folderPath) => {
        cliFlowStepAnswered({
          question: t('generate_command.web_method.choose_filename'),
          questionKey: 'generate_command.web_method.choose_filename',
          answer: folderPath,
        });
        onSubmit(folderPath);
        goToNextStep();
      }}
      validate={validateWebMethodFolder}
    />
  );
};
